// src/pages/InvoiceGenerator.js
import React, { useState } from "react";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import { useClients } from "../hooks/useClients";

import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.default;

// 🔄 Convert Firebase Storage image URL → Base64 (required for pdfMake)
async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}


// ---------- Helpers ----------

// Convert number to words (basic, enough for invoices)
function numberToWords(num) {
  if (num === 0) return "Zero";

  const belowTwenty = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const scales = ["", "Thousand", "Million", "Billion"];

  function chunk(n) {
    const words = [];

    if (n >= 100) {
      words.push(belowTwenty[Math.floor(n / 100)] + " Hundred");
      n = n % 100;
    }

    if (n >= 20) {
      words.push(tens[Math.floor(n / 10)]);
      if (n % 10) words.push(belowTwenty[n % 10]);
    } else if (n > 0) {
      words.push(belowTwenty[n]);
    }

    return words.join(" ");
  }

  let word = "";
  let scaleIndex = 0;

  while (num > 0) {
    const part = num % 1000;
    if (part > 0) {
      const partWords = chunk(part);
      const scaleWord = scales[scaleIndex];
      word =
        partWords +
        (scaleWord ? " " + scaleWord : "") +
        (word ? " " + word : "");
    }
    num = Math.floor(num / 1000);
    scaleIndex++;
  }

  return word;
}

function amountToWordsAED(amount) {
  const integerPart = Math.floor(amount);
  const fractionPart = Math.round((amount - integerPart) * 100);

  let words = numberToWords(integerPart) + " Dirhams";
  if (fractionPart > 0) {
    words += " and " + numberToWords(fractionPart) + " Fils";
  }
  words += " Only";
  return words;
}

// Month label like "OCT 2025"
function monthLabelFromRange(fromDate, toDate) {
  if (!fromDate) return "";
  const d = new Date(fromDate);
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const m = months[d.getMonth()];
  const y = d.getFullYear();
  return `${m} ${y}`;
}

function formatAED(n) {
  return (n || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---------- Component ----------

export default function InvoiceGenerator() {
  const todayYMD = new Date().toISOString().slice(0, 10);

  const { clientId, clientList, handleClientSelect } = useClients();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayYMD);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [loading, setLoading] = useState(false);
  // NEW: Extra manual invoice rows
const [extraRows, setExtraRows] = useState([]);

// NEW: Extra notes
const [extraNotes, setExtraNotes] = useState("");

// NEW: Show single-trip details
const [showTripDetails, setShowTripDetails] = useState(false);


  const selectedClient =
    clientList.find((c) => c.id === clientId) || { name: "" };

  async function generateInvoice() {
    if (!clientId) {
      alert("Select a client first.");
      return;
    }
    if (!fromDate || !toDate) {
      alert("Select From and To dates.");
      return;
    }
    if (!invoiceNumber) {
      alert("Enter Invoice Number.");
      return;
    }

    setLoading(true);

    try {
      // 1) Load all tripImports folders
      const importsSnap = await getDocs(collection(db, "tripImports"));

      const trips = [];

      for (const folder of importsSnap.docs) {
        const batchKey = folder.id; // e.g. koi_technologies_fzco_2025-11-13

        const parts = batchKey.split("_");
        const date = parts[parts.length - 1]; // "2025-11-13"
        const cId = parts.slice(0, parts.length - 1).join("_");

        // Only this client
        if (cId !== clientId) continue;

        // Date range filter
        if (date < fromDate || date > toDate) continue;

        const tripsRef = collection(db, `tripImports/${batchKey}/trips`);
        const tripsSnap = await getDocs(tripsRef);

        tripsSnap.forEach((t) =>
          trips.push({
            id: t.id,
            ...t.data(),
            batchKey,
            batchDate: date,
          })
        );
      }

      if (trips.length === 0) {
        alert("No trips found for this client & date range.");
        setLoading(false);
        return;
      }

      // 2) Calculate invoice figures (ALL trips, not only done)
      const tripCount = trips.length;

      const transportAmount = trips.reduce(
        (sum, t) => sum + (Number(t.clientFinalPrice) || 0),
        0
      );

      const parkingAmount = trips.reduce(
        (sum, t) => sum + (Number(t.clientParking) || 0),
        0
      );

      const grandTotal = transportAmount + parkingAmount;

// ✅ include manual add/sub rows
const extraTotal = extraRows.reduce((sum, r) => {
  const val = Number(r.amount) || 0;
  return sum + (r.type === "add" ? val : -val);
}, 0);

const finalTotal = grandTotal + extraTotal;

// ✅ words should follow the final total
const amountInWords = amountToWordsAED(finalTotal);

      const monthLabel = monthLabelFromRange(fromDate, toDate);

      // 3) Build PDF using pdfmake
      const clientName = selectedClient.name || clientId;
      const companyName = "Comfort Line Luxury Motor Vehicles Services LLC";
      const companyAddress =
        "Office 101A, UAE Enterprises Building Airport Road Dubai, UAE";
      const subjectText = `Guest Transportation Services for ${monthLabel}`;

      // 🔹 Load letterhead image from Firebase Storage and convert to Base64
      const letterheadURL =
        "https://firebasestorage.googleapis.com/v0/b/cll-dashboard-93e97.firebasestorage.app/o/companyAssets%2Fletterheads%2Fmz8keZlpO2hp2DNer5Ky_1761515449346_WhatsApp%20Image%202025-10-27%20at%2001.50.08.jpeg?alt=media&token=a8098f1b-75bc-4f69-8d90-0ca343913756";

      const base64Letterhead = await urlToBase64(letterheadURL);

      const docDefinition = {
        // ✅ Use letterhead as page background (full page)
        background: {
          image: base64Letterhead,
          width: 595, // A4 width in pdfMake points
        },

        content: [
          // ⭐ START CONTENT BELOW LETTERHEAD
          {
            text: "INVOICE",
            style: "invoiceTitle",
            alignment: "center",
            margin: [0, 0, 0, 10],
          },

          {
            columns: [
              {
                width: "*",
                stack: [
                  { text: companyName, style: "companyName" },
                  { text: companyAddress, style: "small" },
                ],
              },
              {
                width: "auto",
                table: {
                  widths: ["auto", "auto"],
                  body: [
                    [
                      { text: "Invoice No:", bold: true, alignment: "right" },
                      { text: invoiceNumber },
                    ],
                    [
                      { text: "Date:", bold: true, alignment: "right" },
                      { text: invoiceDate },
                    ],
                  ],
                },
                layout: "noBorders",
              },
            ],
            margin: [0, 0, 0, 15],
          },

          {
            text: "Bill To:",
            style: "sectionHeader",
          },
          {
            text: clientName,
            margin: [0, 0, 0, 10],
          },

          {
            text: "Subject:",
            style: "sectionHeader",
            margin: [0, 0, 0, 2],
          },
          {
            text: subjectText,
            margin: [0, 0, 0, 15],
          },

          {
            style: "tableExample",
            table: {
              headerRows: 1,
              widths: [30, "*", 60, 70, 80, 80],
              body: (function () {
  const rows = [];

  // HEADER
  rows.push([
    { text: "S.N", style: "tableHeader" },
    { text: "DESCRIPTION", style: "tableHeader" },
    { text: "No. of Trips", style: "tableHeader" },
    { text: "Month", style: "tableHeader" },
    { text: "Amount (AED)", style: "tableHeader" },
    { text: "TOTAL (AED)", style: "tableHeader" },
  ]);

  rows.push([
  "1",
  { text: "Guest Transportation Service", noWrap: true },
  tripCount.toString(),
  monthLabel,
  { text: formatAED(transportAmount), alignment: "right" },
  { text: formatAED(transportAmount), alignment: "right" }
]);


  rows.push([
  "2",
  { text: "Airport Parking Fee", noWrap: true },
  "-",
  monthLabel,
  { text: formatAED(parkingAmount), alignment: "right" },
  { text: formatAED(parkingAmount), alignment: "right" }
]);


  // 3) EXTRA invoice rows (Dynamic)
extraRows.forEach((item, idx) => {
  const serial = rows.length;

  const val = Number(item.amount) || 0;
const computed = item.type === "add" ? val : -val;


  rows.push([
    serial.toString(),
    { text: item.desc, noWrap: true },   // description fix
    "-",
    monthLabel,
    { text: formatAED(computed), alignment: "right" },     // amount column
    { text: formatAED(computed), alignment: "right" },     // total column
  ]);
});


  
  rows.push([
  {
    text: "TOTAL",
    colSpan: 5,
    bold: true,
    alignment: "right",
    margin: [0, 3, 10, 3]  // slight right padding for professional spacing
  },
  {}, {}, {}, {}, 
  {
    text: formatAED(finalTotal),
    bold: true,
    alignment: "right",
    margin: [0, 3, 0, 3]
  }
]);



    return rows;
})()
            },
          layout: {
  // Horizontal lines
  hLineWidth: function (i, node) {
    const lastRow = node.table.body.length - 1;

    // Strong line above TOTAL row
    if (i === lastRow) return 0.7;

    // Normal row lines
    return 0.5;
  },
  hLineColor: function (i, node) {
    const lastRow = node.table.body.length - 1;

    // Strong separator above TOTAL
    if (i === lastRow) return "#000";

    // Light grey for normal rows
    return "#ccc";
  },

  // Vertical lines (ENTIRELY OFF)
  vLineWidth: function () {
    return 0;   // 👈 No vertical borders
  },
  vLineColor: function () {
    return "#fff"; // Doesn’t matter since width is 0
  },

  paddingLeft: function () {
    return 4;
  },
  paddingRight: function () {
    return 4;
  },
},


          margin: [0, 0, 0, 15]
        },
  
        // Extra notes (always optional)
        ...(extraNotes.trim() !== ""
          ? [
              {
                text: extraNotes,
                margin: [0, 10, 0, 15],
                italics: true,
              },
            ]
          : []),


          {
            text: `Amount in words: ${amountInWords}`,
            italics: true,
            margin: [0, 0, 0, 20],
          },

          {
            text: "Bank Details:",
            style: "sectionHeader",
            margin: [0, 0, 0, 4],
          },
          {
            ul: [
              "Account Title: Comfort Line Luxury Motor Vehicles Services LLC",
              "Account No.: 1000473577",
              "IBAN: AE70 0080 0000 0100 0473 577",
              "Bank Name: Al Masraf - Arab Bank For Investment & Foreign Trade",
              "Branch Name: Main Branch, Abu Dhabi",
            ],
            margin: [0, 0, 0, 20],
          },

          {
            columns: [
              {
                text: "For " + companyName,
              },
            ],
            margin: [0, 40, 0, 0],
          },
          {
            text: "Authorised Signatory",
            margin: [0, 20, 0, 0],
          },
        ],

        styles: {
          invoiceTitle: {
            fontSize: 20,
            bold: true,
          },
          companyName: {
            fontSize: 12,
            bold: true,
            margin: [0, 0, 0, 2],
          },
          small: {
            fontSize: 9,
          },
          sectionHeader: {
            fontSize: 11,
            bold: true,
          },
          tableHeader: {
            bold: true,
            fontSize: 10,
          },
        },
        defaultStyle: {
          fontSize: 10,
        },

        // ✅ Increase top margin so text starts below letterhead design
        pageMargins: [40, 160, 40, 40],
      };


      const fileName = `Invoice_${clientId}_${fromDate}_${toDate}.pdf`;
      pdfMake.createPdf(docDefinition).download(fileName);
    } catch (err) {
      console.error(err);
      alert("Failed to generate invoice.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>📄 Invoice Generator</h2>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label>Client:</label>
        <select value={clientId} onChange={handleClientSelect}>
          <option value="">Select client</option>
          {clientList.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="add_new">➕ Add New Client</option>
        </select>

        <label>From:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />

        <label>To:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <label>Invoice Date:</label>
        <input
          type="date"
          value={invoiceDate}
          onChange={(e) => setInvoiceDate(e.target.value)}
        />

        <label>Invoice No:</label>
        <input
          type="text"
          value={invoiceNumber}
          onChange={(e) => setInvoiceNumber(e.target.value)}
          placeholder="e.g. IN-OCT-2025-0005"
          style={{ minWidth: 180 }}
        />
        {/* --- EXTRA INVOICE ITEMS --- */}
<div style={{ width: "100%", marginTop: 10 }}>
  <h4>Additional Invoice Items</h4>

  {extraRows.map((row, i) => (
    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
      <input
        type="text"
        placeholder="Description"
        value={row.desc}
        onChange={(e) => {
          const newRows = [...extraRows];
          newRows[i].desc = e.target.value;
          setExtraRows(newRows);
        }}
        style={{ flex: 2 }}
      />

      <input
        type="number"
        placeholder="Amount"
        value={row.amount}
        onChange={(e) => {
          const newRows = [...extraRows];
          newRows[i].amount = Number(e.target.value);
          setExtraRows(newRows);
        }}
        style={{ width: 120 }}
      />

      <select
        value={row.type}
        onChange={(e) => {
          const newRows = [...extraRows];
          newRows[i].type = e.target.value;
          setExtraRows(newRows);
        }}
        style={{ width: 120 }}
      >
        <option value="add">Add</option>
        <option value="subtract">Subtract</option>
      </select>

      <button
        onClick={() => setExtraRows(extraRows.filter((_, x) => x !== i))}
        style={{ background: "red", color: "white", borderRadius: 4 }}
      >
        ✖
      </button>
    </div>
  ))}

  <button
    onClick={() =>
      setExtraRows([...extraRows, { desc: "", amount: 0, type: "add" }])
    }
    style={{ marginTop: 6 }}
  >
    ➕ Add Item
  </button>
</div>
{/* --- TRIP DETAILS OPTION --- */}
<div style={{ marginTop: 12 }}>
  <label>
    <input
      type="checkbox"
      checked={showTripDetails}
      onChange={() => setShowTripDetails(!showTripDetails)}
    />
    &nbsp; Show detailed trip info

  </label>
</div>
{/* --- EXTRA NOTES --- */}
<div style={{ width: "100%", marginTop: 12 }}>
  <label>Extra Notes:</label>
  <textarea
    value={extraNotes}
    onChange={(e) => setExtraNotes(e.target.value)}
    style={{ width: "100%", minHeight: 60 }}
  ></textarea>
</div>


        <button
          onClick={generateInvoice}
          disabled={loading}
          style={{
            padding: "8px 16px",
            background: "#0066cc",
            color: "#fff",
            borderRadius: 6,
            fontWeight: 600,
            border: "none",
          }}
        >
          {loading ? "Generating..." : "Generate Invoice PDF"}
        </button>
      </div>

      <p style={{ fontSize: 12, color: "#555" }}>
        Note: Invoice uses <b>all trips</b> for the client between the selected
        dates (even if not marked as done). If you update pricing or parking
        later, just generate the invoice again — it will always use latest data.
      </p>
    </div>
  );
}
